import { memo, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '@aimsos/i18n';
import { FormSelect, NumericField, Textfield } from '@aimsos/ui';

import { matchesPattern } from '../utils/displaySettings.js';
import { humanizeKey } from '../utils/humanizeKey.js';
import SettingToggle from './SettingToggle.jsx';

/**
 * One of the type's own look-and-feel options (CT-5579).
 *
 * The control comes from the schema, not from a branch per property:
 * `readDisplaySettings` already turned `enum` into a select, `boolean` into a
 * toggle, `integer` into a number and `string` into text, so this renders what
 * it was handed.
 *
 * ## A shape is checked on blur, not while typing
 *
 * Six properties state a `pattern` — a currency is three capitals, an action
 * code is `^[A-Z0-9_]{1,100}$`, a language is `en` or `en-GB`. A length cap
 * and a numeric range are handed to the control and enforced as the author
 * types; a shape cannot be, because `e` is on the way to `en`. So it is
 * checked when they leave the field, the way `WidgetIdentityFields` surfaces
 * the missing name — flagging it on the first keystroke would be an error
 * message for having started.
 *
 * It flags, it does not block: the slot and grouper gaps beside it are notes
 * too, and the backend refuses the value at publish either way.
 *
 * ## Nothing is pre-filled
 *
 * No `display` property in any of the 28 types declares a `default`, so the
 * builder does not know what the backend assumes for one it never sends.
 * Every control therefore starts empty — a select on its placeholder, a toggle
 * off — and `toDisplayConfig` drops what the author did not touch. A toggle
 * reads "off" while meaning "unset", which is the one place this is lossy and
 * the reason the defaults belong in the schema.
 */
const DisplaySettingField = memo(function DisplaySettingField({ field, value, onChange }) {
  const { t } = useTranslation('dashboards');
  const [touched, setTouched] = useState(false);

  const label = t(`widgetBuilder.displaySettings.names.${field.key}`, {
    defaultValue: humanizeKey(field.key),
  });

  const options = useMemo(
    () => (field.options ?? []).map((option) => ({
      value: option,
      label: t(`widgetBuilder.displaySettings.values.${option}`, {
        defaultValue: humanizeKey(option),
      }),
    })),
    [field.options, t],
  );

  const handleSelect = useCallback((next) => onChange(field.path, next), [field.path, onChange]);
  const handleToggle = useCallback(
    () => onChange(field.path, !value),
    [field.path, onChange, value],
  );
  const handleNumber = useCallback(
    (next) => onChange(field.path, next ?? ''),
    [field.path, onChange],
  );
  // `Textfield` hands over the string, not the event — see its own JSDoc.
  const handleText = useCallback((next) => onChange(field.path, next), [field.path, onChange]);
  const handleBlur = useCallback(() => setTouched(true), []);

  const isMalformed = touched && !matchesPattern(value, field.pattern);
  const hint = isMalformed
    ? t(`widgetBuilder.displaySettings.hints.${field.key}`, {
      defaultValue: t('widgetBuilder.displaySettings.invalidFormat'),
    })
    : '';

  // The toggle carries its own label, so it is not wrapped in one.
  if (field.control === 'toggle') {
    return <SettingToggle checked={Boolean(value)} label={label} onChange={handleToggle} />;
  }

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <span className="truncate text-xs font-medium text-content-primary">{label}</span>

      {field.control === 'select' ? (
        <FormSelect
          value={value ?? ''}
          onChange={handleSelect}
          options={options}
          placeholder={t('widgetBuilder.displaySettings.placeholder')}
          portalListbox
        />
      ) : null}

      {field.control === 'number' ? (
        <NumericField
          value={value === '' || value === undefined || value === null ? null : Number(value)}
          onValueChange={handleNumber}
          // The schema states the range and the backend enforces it, so the
          // control that collects the number may as well hold the author to
          // it: a donut takes 2–20 slices and an AI summary 100–4000
          // characters, and finding that out at publish is finding it out
          // three clicks later.
          min={field.min ?? undefined}
          max={field.max ?? undefined}
          placeholder={t('widgetBuilder.displaySettings.placeholder')}
          aria-label={label}
        />
      ) : null}

      {field.control === 'text' ? (
        <Textfield
          value={value ?? ''}
          onChange={handleText}
          onBlur={handleBlur}
          maxLength={field.maxLength ?? undefined}
          state={isMalformed ? 'error' : 'default'}
          supportingText={hint}
          aria-invalid={isMalformed || undefined}
          placeholder={t('widgetBuilder.displaySettings.placeholder')}
          aria-label={label}
        />
      ) : null}
    </div>
  );
});

DisplaySettingField.propTypes = {
  /** One entry of `readDisplaySettings().fields`. */
  field: PropTypes.shape({
    path: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
    control: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.string),
    maxLength: PropTypes.number,
    pattern: PropTypes.string,
    min: PropTypes.number,
    max: PropTypes.number,
  }).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  onChange: PropTypes.func.isRequired,
};

export default DisplaySettingField;
